import type { AccountView, OAuthProviderSlug } from "@kwp/contracts";
import { AccountColor } from "./AccountColor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type ReactNode } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { firebaseTokenSource } from "../../lib/auth-token";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { Button, cx, ErrorText, PageHeader, Section, StatusText } from "../../ui";
import { Dialog } from "../../ui/overlay";
import { accountHealth, connectErrorMessage, ConnectError, isBusy, PROVIDER_LABEL, PROVIDER_SLUG, startConnect, STATUS, syncedAgo } from "./accounts-model";
import { useNow } from "../tasks/use-tasks";
import { MeetScopeBadge } from "../meet/MeetScopeBadge";
import { DefaultFromSection } from "./DefaultFromSection";
import { SettingsNav } from "./SettingsNav";

/**
 * Settings → Mail accounts (architecture §5): every connected mailbox with its workspace and
 * status, the person's default From per workspace, connect Google / Microsoft, reconnect, move to
 * another workspace, disconnect. The
 * provider redirects back here with `?connected=<accountId>` or `?error=<reason>`.
 */
export function AccountsPage({ connected, error, onDismiss }: { connected: string | undefined; error: string | undefined; onDismiss: () => void }) {
  const trpc = useTRPC();
  const accounts = useQuery({
    ...trpc.accounts.list.queryOptions(),
    // Poll while a mailbox is connecting or syncing, so its status settles on screen.
    refetchInterval: (q) => (q.state.data !== undefined && q.state.data.some((a) => isBusy(a.status)) ? 5000 : false),
  });
  const [failure, setFailure] = useState<string | null>(null);
  const connect = useConnect(setFailure);
  const list = accounts.data === undefined ? [] : accounts.data;
  const justConnected = connected === undefined ? undefined : list.find((a) => a.id === connected);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Mail accounts" />
      <SettingsNav />
      {connected !== undefined ? (
        <Banner tone="ok" onDismiss={onDismiss}>
          {justConnected === undefined ? "Mailbox connected." : `Connected ${justConnected.address}.`} The first sync runs now; mail appears in the inbox as it arrives.
        </Banner>
      ) : null}
      {error !== undefined ? (
        <Banner tone="bad" onDismiss={onDismiss}>
          {connectErrorMessage(error)}
        </Banner>
      ) : null}
      <Section title="Your mailboxes" description="Each mailbox belongs to you and one workspace. Nobody else sees it.">
        {accounts.isPending ? <StatusText>Loading accounts…</StatusText> : null}
        {accounts.isError ? <ErrorText>{errorMessage(accounts.error)}</ErrorText> : null}
        {accounts.isSuccess && list.length === 0 ? <p className="text-sm text-muted">No mailbox is connected yet.</p> : null}
        {list.length > 0 ? <AccountsTable accounts={list} onReconnect={(a) => connect.start(PROVIDER_SLUG[a.provider], a.workspaceId)} busy={connect.busy} /> : null}
      </Section>
      <DefaultFromSection accounts={list} />
      <ConnectSection onConnect={connect.start} busy={connect.busy} failure={failure} />
    </div>
  );
}

function useConnect(setFailure: (message: string | null) => void) {
  const { config, firebase } = useClients();
  const [busy, setBusy] = useState(false);
  return {
    busy,
    start: (provider: OAuthProviderSlug, workspaceId: string) => {
      setBusy(true);
      setFailure(null);
      startConnect(window.fetch.bind(window), config.apiUrl, firebaseTokenSource(firebase.auth), provider, workspaceId).then(
        (url) => window.location.assign(url),
        (e: unknown) => {
          setBusy(false);
          setFailure(e instanceof ConnectError ? e.message : errorMessage(e));
        },
      );
    },
  };
}

function ConnectSection({ onConnect, busy, failure }: { onConnect: (p: OAuthProviderSlug, ws: string) => void; busy: boolean; failure: string | null }) {
  const me = useMe();
  const id = useId();
  const first = me.workspaces[0];
  const [workspaceId, setWorkspaceId] = useState(first === undefined ? "" : first.id);
  return (
    <Section title="Connect a mailbox" description="You sign in at Google or Microsoft; KitWork+ never sees your password.">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={id} className="text-sm font-medium text-text">
            Workspace
          </label>
          <select id={id} value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} className="min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text">
            {me.workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <Button variant="primary" disabled={busy || workspaceId === ""} onClick={() => onConnect("google", workspaceId)}>
          Connect Google
        </Button>
        <Button disabled={busy || workspaceId === ""} onClick={() => onConnect("microsoft", workspaceId)}>
          Connect Microsoft
        </Button>
      </div>
      {failure === null ? null : (
        <div className="mt-3">
          <ErrorText>{failure}</ErrorText>
        </div>
      )}
    </Section>
  );
}

function AccountsTable({ accounts, onReconnect, busy }: { accounts: readonly AccountView[]; onReconnect: (a: AccountView) => void; busy: boolean }) {
  const [disconnecting, setDisconnecting] = useState<AccountView | null>(null);
  const now = useNow();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">
              Address
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Workspace
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Colour
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Status
            </th>
            <th scope="col" className="py-2 font-medium">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.id} className="border-t border-border align-top">
              <td className="py-2 pr-4">
                <span className="block font-medium text-text">{a.address}</span>
                <span className="block text-xs text-muted">{PROVIDER_LABEL[a.provider]}</span>
                <MeetScopeBadge account={a} />
              </td>
              <td className="py-2 pr-4">
                <MoveSelect account={a} />
              </td>
              <td className="py-2 pr-4">
                <AccountColor account={a} />
              </td>
              <td className="py-2 pr-4">
                <StatusBadge account={a} now={now} />
              </td>
              <td className="py-2">
                <div className="flex flex-wrap justify-end gap-2">
                  {accountHealth(a, now) !== "ok" ? (
                    <Button variant="primary" disabled={busy} onClick={() => onReconnect(a)} aria-label={`Reconnect ${a.address}`}>
                      Reconnect
                    </Button>
                  ) : null}
                  <Button variant="danger" onClick={() => setDisconnecting(a)} aria-label={`Disconnect ${a.address}`}>
                    Disconnect
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {disconnecting === null ? null : <DisconnectDialog account={disconnecting} onClose={() => setDisconnecting(null)} />}
    </div>
  );
}

export function StatusBadge({ account, now }: { account: Pick<AccountView, "status" | "statusDetail" | "lastSyncedAt">; now: Date }) {
  // A ready mailbox that stopped syncing says so (§5), not "Ready".
  const s = accountHealth(account, now) === "stale" ? STALE : STATUS[account.status];
  return (
    <span className="flex flex-col gap-0.5">
      <span
        className={cx(
          "inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
          s.tone === "ok" && "border-border text-text",
          s.tone === "busy" && "border-border text-muted",
          s.tone === "bad" && "border-danger text-danger",
        )}
      >
        <span aria-hidden="true" className={cx("size-1.5 rounded-full", s.tone === "ok" ? "bg-green-600" : s.tone === "busy" ? "bg-amber-500" : "bg-danger")} />
        {s.label}
      </span>
      {account.lastSyncedAt === null ? null : <span className="text-xs text-muted">Last synced {syncedAgo(account.lastSyncedAt, now)}</span>}
      {account.statusDetail === null ? null : <span className="text-xs text-muted">{account.statusDetail}</span>}
    </span>
  );
}

const STALE = { label: "Not syncing", tone: "bad" } as const;

function MoveSelect({ account }: { account: AccountView }) {
  const me = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const id = useId();
  const move = useMutation(
    trpc.accounts.move.mutationOptions({ onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() }) }),
  );
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="sr-only">
        Workspace of {account.address}
      </label>
      <select
        id={id}
        value={account.workspaceId}
        disabled={move.isPending}
        onChange={(e) => move.mutate({ accountId: account.id, workspaceId: e.target.value })}
        className="min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
      >
        {me.workspaces.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>
      {move.isError ? <ErrorText>{errorMessage(move.error)}</ErrorText> : null}
    </div>
  );
}

function DisconnectDialog({ account, onClose }: { account: AccountView; onClose: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const disconnect = useMutation(
    trpc.accounts.disconnect.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() });
        onClose();
      },
    }),
  );
  return (
    <Dialog
      title={`Disconnect ${account.address}?`}
      description="KitWork+ stops syncing it and forgets its cached mail. Nothing is deleted from the mailbox itself."
      onClose={onClose}
    >
      {disconnect.isError ? <ErrorText>{errorMessage(disconnect.error)}</ErrorText> : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={disconnect.isPending} onClick={() => disconnect.mutate({ accountId: account.id })}>
          {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
        </Button>
      </div>
    </Dialog>
  );
}

function Banner({ tone, onDismiss, children }: { tone: "ok" | "bad"; onDismiss: () => void; children: ReactNode }) {
  return (
    <div role={tone === "bad" ? "alert" : "status"} className={cx("flex items-start gap-3 rounded-lg border p-3 text-sm", tone === "bad" ? "border-danger text-danger" : "border-border text-text")}>
      <p className="flex-1">{children}</p>
      <Button variant="ghost" className="min-h-8" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
